import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { settingsAPI } from '../../api';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin, isTeamMember } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appSettings, setAppSettings] = useState({
    appName: 'Earth Warrior',
    logo: null,
    favicon: null
  });

  // Check if on portal page - hide menu items
  const isPortalPage = location.pathname === '/' || location.pathname === '/portal';

  // Fetch app settings (logo, name, favicon)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data?.data) {
          const { appName, logo, favicon } = response.data.data;
          setAppSettings({
            appName: appName || 'Earth Warrior',
            logo: logo || null,
            favicon: favicon || null
          });

          // Update favicon dynamically
          if (favicon) {
            const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = favicon;
            document.getElementsByTagName('head')[0].appendChild(link);
          }

          // Update page title
          if (appName) {
            document.title = appName;
          }
        }
      } catch (err) {
        // Use defaults if settings fetch fails
        console.log('Using default settings');
      }
    };
    fetchSettings();
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowDropdown(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.navbar-profile')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          {appSettings.logo ? (
            <img src={appSettings.logo} alt={appSettings.appName} className="navbar-logo" />
          ) : null}
          <span className="navbar-brand-text">{appSettings.appName}</span>
        </Link>

        {/* Hamburger button - only shows on mobile (not on portal page) */}
        {!isPortalPage && (
          <button className="hamburger-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        )}

        {!isPortalPage && (
          <div className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <>
                    <Link to="/dashboard" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <Link to="/samples" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Samples</Link>
                    <Link to="/parameters" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Parameters</Link>
                    <Link to="/master-data" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Source</Link>
                    <Link to="/team" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Team</Link>
                    <Link to="/settings" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Settings</Link>
                  </>
                )}
                {isTeamMember && (
                  <>
                    <Link to="/my-samples" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>My Samples</Link>
                    <Link to="/create-sample" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>New Sample</Link>
                    <Link to="/analyse" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Analyse</Link>
                  </>
                )}

                {/* Mobile profile section - only visible in mobile menu */}
                <div className="mobile-profile-section">
                  <div className="mobile-profile-header">
                    <span className="navbar-avatar">{user?.name?.charAt(0).toUpperCase()}</span>
                    <div className="mobile-profile-info">
                      <span className="mobile-profile-name">{user?.name}</span>
                      <span className="mobile-profile-role">{user?.role}</span>
                    </div>
                  </div>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="mobile-logout-btn">
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/portal" className="navbar-link" onClick={() => setMobileMenuOpen(false)}>Portal</Link>
                <Link to="/login" className="btn btn-primary" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              </>
            )}
          </div>
        )}

        {/* Overlay for mobile menu */}
        {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>}

        {/* Desktop profile dropdown - hidden on mobile */}
        {!isPortalPage && isAuthenticated && (
          <div className="navbar-profile desktop-only">
            <button
              className="navbar-profile-btn"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <span className="navbar-avatar">{user?.name?.charAt(0).toUpperCase()}</span>
              <span className="navbar-username">{user?.name}</span>
              <span className="navbar-dropdown-icon">▼</span>
            </button>

            {showDropdown && (
              <div className="navbar-dropdown">
                <div className="navbar-dropdown-header">
                  <span className="navbar-dropdown-name">{user?.name}</span>
                  <span className="navbar-dropdown-role">{user?.role}</span>
                </div>
                <div className="navbar-dropdown-divider"></div>
                <button onClick={handleLogout} className="navbar-dropdown-item navbar-logout-btn">
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
